import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { RefreshCw, X, ClipboardList, TrendingUp } from "lucide-react";

const MARKETS = [
  { code: "US", label: "United States" },
  { code: "CA", label: "Canada" },
];

function opportunityBadgeClass(score) {
  if (score >= 5000) return "bg-emerald-100 text-emerald-700 border-emerald-300";
  if (score >= 1000) return "bg-amber-100 text-amber-700 border-amber-300";
  return "bg-slate-100 text-slate-500 border-slate-300";
}

export default function AdminTopicIdeas() {
  const [market, setMarket] = useState("US");
  const [topics, setTopics] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const navigate = useNavigate();

  const load = (m) => {
    setTopics(null);
    api.get(`/admin/newsroom/topics?status=new&market=${m}`).then((res) => setTopics(res.data)).catch(() => {
      setTopics([]);
      toast.error("Failed to load topic ideas");
    });
  };
  useEffect(() => { load(market); }, [market]);

  const refreshNow = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post(`/admin/newsroom/topics/refresh?market=${market}`);
      if (data.throttled) {
        toast.info(data.message);
      } else {
        toast.success(data.new_topics > 0 ? `Found ${data.new_topics} new topic(s)` : "No new topics right now");
      }
      load(market);
    } catch {
      toast.error("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const dismiss = async (id) => {
    await api.post(`/admin/newsroom/topics/${id}/dismiss`);
    setTopics((prev) => prev.filter((t) => t.id !== id));
    toast.success("Dismissed");
  };

  const generateBrief = async (topic) => {
    setGeneratingId(topic.id);
    try {
      await api.post("/admin/newsroom/briefs/generate", {
        source_type: "topic_idea",
        source_id: topic.id,
        topic: topic.keyword,
        pillar: topic.pillar,
        content_type: "evergreen_guide",
        market,
      });
      setTopics((prev) => prev.filter((t) => t.id !== topic.id));
      toast.success("Brief generated");
      navigate("/admin/briefs");
    } catch {
      toast.error("Brief generation failed");
    } finally {
      setGeneratingId(null);
    }
  };

  return (
    <div data-testid="admin-topic-ideas-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-serif-heading font-bold text-navy">Topic Ideas</h1>
          <p className="text-sm text-slate-500 mt-1">Real search-volume data from DataForSEO — topics not yet covered on the site, ranked by opportunity (volume × low competition).</p>
        </div>
        <Button onClick={refreshNow} disabled={refreshing} data-testid="topics-refresh-button" className="bg-gold text-navy hover:bg-gold/90">
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Researching..." : `Refresh Now (${market})`}
        </Button>
      </div>

      <Tabs value={market} onValueChange={setMarket} className="mb-4">
        <TabsList data-testid="topic-ideas-market-tabs">
          {MARKETS.map((m) => (
            <TabsTrigger key={m.code} value={m.code} data-testid={`topic-ideas-market-tab-${m.code}`}>{m.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Opportunity</TableHead><TableHead>Keyword</TableHead><TableHead>Pillar</TableHead>
              <TableHead>Search Volume</TableHead><TableHead>Competition</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {topics?.map((t) => (
              <TableRow key={t.id} data-testid={`topic-item-${t.id}`}>
                <TableCell>
                  <Badge className={`border ${opportunityBadgeClass(t.opportunity_score)}`} data-testid={`topic-opportunity-${t.id}`}>
                    <TrendingUp className="w-3 h-3 mr-1" /> {Math.round(t.opportunity_score).toLocaleString()}
                  </Badge>
                </TableCell>
                <TableCell className="font-medium text-navy capitalize">
                  {t.keyword}
                  {t.long_tail && <Badge className="ml-2 bg-blue-50 text-blue-600 border-blue-200 border text-[10px]" data-testid={`topic-longtail-${t.id}`}>Long-tail</Badge>}
                </TableCell>
                <TableCell><Badge variant="secondary">{t.pillar}</Badge></TableCell>
                <TableCell className="text-sm text-slate-600">{t.search_volume?.toLocaleString()}/mo</TableCell>
                <TableCell className="text-sm text-slate-500">{Math.round((t.competition ?? 0) * 100)}%</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button size="sm" variant="outline" onClick={() => generateBrief(t)} disabled={generatingId === t.id} data-testid={`topic-brief-button-${t.id}`}>
                    <ClipboardList className="w-3.5 h-3.5 mr-1" /> {generatingId === t.id ? "Generating..." : "Generate Brief"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => dismiss(t.id)} data-testid={`topic-dismiss-button-${t.id}`}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {topics?.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">No topic ideas yet for {market}. Click "Refresh Now" to research keywords.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
